import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { ShieldCheck, FileText, Loader2, LogOut, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MainSiteLink from "@/components/MainSiteLink";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function EvaluatorConsent() {
  const { t } = useTranslation();
  const KEY_POINTS = t("evaluator.consent.key_points", { returnObjects: true });
  const DOCUMENT = t("evaluator.consent.document", { returnObjects: true });
  const { refreshUser, logout } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  const accept = async () => {
    setSaving(true);
    try {
      await api.post("/evaluator/consent");
      await refreshUser();
      toast.success(t("evaluator.consent.accepted"));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("evaluator.consent.failed"));
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="evaluator-consent">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary-foreground" strokeWidth={1.5} />
            </div>
            <p className="font-heading text-base font-semibold tracking-tight">{t("evaluator.brand")}</p>
          </div>
          <div className="flex items-center gap-4">
            <MainSiteLink />
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => { logout(); }} data-testid="consent-logout">
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("common.sign_out")}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-6 h-6 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{t("evaluator.consent.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("evaluator.consent.subtitle")}</p>
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 space-y-4 text-sm leading-relaxed">
          <p className="font-medium">{t("evaluator.consent.dear")}</p>
          {t("evaluator.consent.intro", { returnObjects: true }).map((para, i) => <p key={i}>{para}</p>)}

          <div className="pt-2">
            <p className="font-medium mb-2">{t("evaluator.consent.key_points_title")}</p>
            <ul className="list-disc pl-5 space-y-1.5 text-muted-foreground">
              {KEY_POINTS.map((k, i) => <li key={i}>{k}</li>)}
            </ul>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" /> {t("evaluator.consent.full_document")}
          </p>
          <div className="max-h-[360px] overflow-y-auto custom-scroll border border-border rounded-lg p-5 bg-muted/20 text-sm space-y-3" data-testid="consent-document">
            <p className="font-heading text-base font-semibold">{t("evaluator.consent.document_title")}</p>
            {DOCUMENT.map((b, i) => (
              <div key={i} className={b.sub ? "pl-1" : "pt-1"}>
                <p className={b.sub ? "font-medium text-primary" : "font-semibold"}>{b.h}</p>
                {(b.p || []).map((para, j) => <p key={j} className="text-muted-foreground mt-1">{para}</p>)}
                {(b.key_points || b.bullets) && (
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground mt-1">
                    {(b.key_points ? KEY_POINTS : b.bullets).map((bl, j) => <li key={j}>{bl}</li>)}
                  </ul>
                )}
                {(b.p2 || []).map((para, j) => <p key={j} className="text-muted-foreground mt-1">{para}</p>)}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm">
          <Trans i18nKey="evaluator.consent.important" components={{ b: <span className="font-semibold" /> }} />
        </div>

        <label className="flex items-start gap-3 mt-6 cursor-pointer" data-testid="consent-checkbox-label">
          <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" data-testid="consent-checkbox" />
          <span className="text-sm">{t("evaluator.consent.checkbox")}</span>
        </label>

        <Button onClick={accept} disabled={!agreed || saving} className="mt-6 h-11 px-6" data-testid="consent-accept-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("evaluator.consent.accept")}
        </Button>
      </main>
    </div>
  );
}
